import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { DropdownButton, MenuItem } from 'react-bootstrap';
import { getOrgTermsContent } from '../../../content/utils.js';
import { RATINGS } from '../../../lib/schema.js';
import { EditInitiativeLink, InitiativeLink } from '../../links/index.jsx';
import RatingLabel from '../../RatingLabel.jsx';
import InitiativeCompletionColumn from './InitiativeCompletionColumn.jsx';
import InitiativeDateColumn from './InitiativeDateColumn.jsx';
import { EyeIcon } from '../../icons/EyeIcon.jsx';
import { EditBlueIcon } from '../../icons/EditBlueIcon.jsx';
import { DeleteBlueIcon } from '../../icons/DeleteBlueIcon.jsx';
import AuthorizedAction from '../../AuthorizedAction.js';
import styles from './InitiativesTableRow.module.css';
import CustomMenuItem from '../../CustomMenuItem.jsx';
import { DuplicateIcon } from '../../icons/DuplicateIcon.jsx';
import DuplicateInitiativeModal from '../duplicate/DuplicateInitiativeModal.jsx';
import { duplicationText } from '../../../constants/initiativeDuplication.js';
import duplicateStyles from '../duplicate/DuplicateInitiativeModal.module.css';

const InitiativesTableRow = ({
  initiative,
  showMandatory,
  showCompletion,
  manageProjectEntities,
  isArchived,
  deleteInitiative,
  project,
  canDuplicate,
  isNewCopy,
}) => {
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);

  const handleInitiativeDelete = () => {
    if (
      window.confirm(
        `Are you sure you want to delete this ${getOrgTermsContent('initiative')}?`
      )
    ) {
      deleteInitiative(initiative.type, initiative.id);
    }
  };

  return (
    <tr
      className={`${styles.initiativesTableRow}${
        isNewCopy ? ` ${duplicateStyles.newCopyRow}` : ''
      }`}
    >
      <td>
        <h3>
          <InitiativeLink initiative={initiative}>
            {initiative.title}
          </InitiativeLink>
          {isNewCopy ? (
            <span className={duplicateStyles.newCopyChip}>
              {duplicationText('newCopy')}
            </span>
          ) : null}
        </h3>
      </td>
      <td>
        <strong
          className={`${initiative.category.archived ? 'text-muted' : ''}`}
        >
          {initiative.category.name}
        </strong>
      </td>
      {showCompletion && <InitiativeCompletionColumn initiative={initiative} />}
      {showMandatory && (
        <td>
          <strong>{initiative.mandatory ? 'Yes' : 'No'}</strong>
        </td>
      )}
      <td>
        <strong>{initiative.surveyId ? 'Yes' : 'No'}</strong>
      </td>
      <td>
        <strong>{initiative.lead}</strong>
      </td>
      <td>
        <strong>
          <RatingLabel rating={initiative.impact} reverse />
        </strong>
      </td>
      <InitiativeDateColumn initiative={initiative} />
      <td>
        <div className="actions flex-inline">
          <DropdownButton
            noCaret
            title=""
            id="id"
            className="btn-more btn btn-default"
          >
            <InitiativeLink initiative={initiative}>
              <CustomMenuItem>
                <span className="flex">
                  <EyeIcon />
                  <span>&nbsp; View</span>
                </span>
              </CustomMenuItem>
              <CustomMenuItem divider />
            </InitiativeLink>

            {manageProjectEntities ? (
              isArchived ? (
                <EditInitiativeLink initiative={initiative}>
                  <CustomMenuItem
                    onClick={() =>
                      window.alert(getOrgTermsContent('archivedProjectMessage'))
                    }
                  >
                    <span className="flex">
                      <EditBlueIcon />
                      <span>&nbsp; Edit</span>
                    </span>
                  </CustomMenuItem>
                  <CustomMenuItem divider />
                </EditInitiativeLink>
              ) : (
                <EditInitiativeLink initiative={initiative}>
                  <CustomMenuItem>
                    <span className="flex">
                      <EditBlueIcon />
                      <span>&nbsp; Edit</span>
                    </span>
                  </CustomMenuItem>
                  <CustomMenuItem divider />
                </EditInitiativeLink>
              )
            ) : null}

            {/* Copying out of an archived project is allowed */}
            {canDuplicate && project ? (
              <span className="item">
                <CustomMenuItem onClick={() => setShowDuplicateModal(true)}>
                  <span className="flex">
                    <DuplicateIcon />
                    <span>&nbsp; {duplicationText('menuItem')}</span>
                  </span>
                </CustomMenuItem>
                <CustomMenuItem divider />
              </span>
            ) : null}

            {manageProjectEntities ? (
              <AuthorizedAction
                action="update"
                subject={`${initiative.type}Initiative`}
              >
                <span className="item">
                  <MenuItem
                    onClick={
                      isArchived
                        ? () =>
                            window.alert(
                              getOrgTermsContent('archivedProjectMessage')
                            )
                        : handleInitiativeDelete
                    }
                  >
                    <span className="flex">
                      <DeleteBlueIcon />
                      <span>&nbsp; Delete</span>
                    </span>
                  </MenuItem>
                  <MenuItem divider />
                </span>
              </AuthorizedAction>
            ) : null}
          </DropdownButton>
          {showDuplicateModal ? (
            <DuplicateInitiativeModal
              show
              initiative={initiative}
              project={project}
              onHide={() => setShowDuplicateModal(false)}
            />
          ) : null}
        </div>
      </td>
    </tr>
  );
};

InitiativesTableRow.propTypes = {
  initiative: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    lead: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    impact: PropTypes.oneOf(RATINGS.HIGH_MEDIUM_LOW).isRequired,
    surveyId: PropTypes.number,
    mandatory: PropTypes.bool,
    completionTracked: PropTypes.bool,
    participationCount: PropTypes.number,
    participationCompletedCount: PropTypes.number,
  }).isRequired,
  showMandatory: PropTypes.bool,
  showCompletion: PropTypes.bool,
  project: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }),
  canDuplicate: PropTypes.bool,
  // Highlights the row of a copy that was just made.
  isNewCopy: PropTypes.bool,
};

InitiativesTableRow.defaultProps = {
  showMandatory: false,
  showCompletion: false,
  project: null,
  canDuplicate: false,
  isNewCopy: false,
};

export default InitiativesTableRow;
